
import React, { useEffect, useRef } from "react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import { cn } from "@/lib/utils";

interface ClientLogo {
  src: string;
  alt: string;
}

const clientLogos: ClientLogo[] = [
  { src: "/img/01-encomenda.png", alt: "Encomenda" },
  { src: "/img/02-susmies.png", alt: "Susmies" },
  { src: "/img/03-4founders.png", alt: "4founders" },
  { src: "/img/04-kintai.png", alt: "Kintai" },
  { src: "/img/05-okticket.png", alt: "OkTicket" },
  { src: "/img/06-premiumguest.png", alt: "Premium Guest" },
  { src: "/img/07-shimoku.png", alt: "Shimoku" },
  { src: "/img/08-improfit.png", alt: "Improfit" },
  { src: "/img/09-breakevent.png", alt: "Breakevent" },
  { src: "/img/10-afsstudio.png", alt: "AFS Studio" },
  { src: "/img/11-hobeen.png", alt: "Hobeen" },
  { src: "/img/12-ferent.png", alt: "Ferent" },
  { src: "/img/13-guimarana.png", alt: "Guimarana" },
  { src: "/img/14-traze.png", alt: "Traze" },
];

const ClientsCarousel = () => {
  const emblaApiRef = useRef<any>(null);
  const autoScrollIntervalRef = useRef<number | null>(null);

  // Constant slow auto-scroll functionality
  useEffect(() => {
    // Detect mobile for faster scroll on small screens
    const isMobile = window.innerWidth <= 800;
    
    // Configuration: faster on mobile (2s), slower on desktop (4s)
    const scrollDelay = isMobile ? 2000 : 4000;
    const scrollOptions = { duration: isMobile ? 500 : 1000 };
    
    const startAutoScroll = () => {
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
      }
      
      autoScrollIntervalRef.current = window.setInterval(() => {
        if (emblaApiRef.current) {
          emblaApiRef.current.scrollNext(scrollOptions);
        }
      }, scrollDelay);
    };
    
    // Apply styling and ensure visibility with will-change for better performance
    const applyStyles = () => {
      document.querySelectorAll('[data-carousel-item]').forEach(slide => {
        if (slide instanceof HTMLElement) {
          slide.style.display = 'flex';
          slide.style.visibility = 'visible';
          slide.style.pointerEvents = 'auto';
          // Add will-change for better GPU acceleration
          slide.style.willChange = 'transform';
        }
      });
      
      document.querySelectorAll('[data-carousel-item] img').forEach(img => {
        if (img instanceof HTMLElement) {
          img.style.visibility = 'visible';
          img.style.display = 'block';
          img.style.pointerEvents = 'auto';
          // Add width and height to prevent layout shift
          img.setAttribute('width', '160');
          img.setAttribute('height', '120');
        }
      });
    };
    
    // Start auto-scroll after a delay to ensure DOM is ready
    const timeout = setTimeout(() => {
      startAutoScroll();
      applyStyles();
      
      // Log for debugging
      console.log("Carousel auto-scroll initialized with slower speed");
    }, 800);
    
    return () => {
      clearTimeout(timeout);
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
      }
      
      // Clean up will-change property to free GPU resources
      document.querySelectorAll('[data-carousel-item]').forEach(slide => {
        if (slide instanceof HTMLElement) {
          slide.style.willChange = 'auto';
        }
      });
    };
  }, []);

  return (
    <Carousel 
      className="w-full my-6"
      opts={{
        align: "start",
        loop: true,
        dragFree: true,
      }}
      setApi={(api) => {
        emblaApiRef.current = api;
      }}
    >
      <CarouselContent className="-ml-4 flex">
        {clientLogos.map((logo, index) => (
          <CarouselItem 
            key={index} 
            className="pl-4 md:basis-1/3 lg:basis-1/4 xl:basis-1/5" 
            data-carousel-item
          >
            <div className={cn(
              "overflow-hidden flex items-center justify-center h-[180px] md:h-[200px]",
            )}>
              <img 
                src={logo.src} 
                alt={logo.alt} 
                loading="lazy"
                width="160"
                height="120"
                className="h-auto max-h-[160px] md:max-h-[180px] w-auto object-contain"
              />
            </div>
          </CarouselItem>
        ))}
      </CarouselContent>
    </Carousel>
  );
};

export default ClientsCarousel;
